// ============================================================================
// offlineCache.ts — durable кэш чтения для ОФЛАЙН-ФЁРСТ (SSoT/SRP).
//
// readThrough(key, fetcher):
//   • связь есть → запрос к серверу (SSoT), успешный ответ СОХРАНЯЕТСЯ;
//   • офлайн (автономный режим ИЛИ транспортный сбой) → последнее сохранённое;
//     нет сохранённого → ЯВНАЯ ошибка (не пустой экран).
// Компоненты показывают баннер «данные из кэша» через useOfflineDataStore
// (stale-ключи) — единый компонент OfflineDataBanner.
// ============================================================================

import { create } from 'zustand';
import { idbGet, idbSet } from '@/lib/storage/idb';
import { isTransportError } from '@/lib/errors';
import { isEffectivelyOffline, markLinkDown } from '@/lib/offlineFlag';
import { markConnectionOffline } from '@/lib/connection';
import { createLogger } from '@/lib/logger';

const log = createLogger('offlineCache');

const PREFIX = 'offline:';

interface CachedEntry<T> {
  data: T;
  savedAt: number;
}

export interface ReadResult<T> {
  data: T;
  fromCache: boolean;
  savedAt: number;
}

// --- stale-ключи (для баннера «данные из кэша») --------------------------------

interface OfflineDataState {
  /** key → время сохранения версии, отданной из кэша (нет ключа = данные свежие). */
  stale: Record<string, number>;
  markStale: (key: string, savedAt: number) => void;
  markFresh: (key: string) => void;
}

export const useOfflineDataStore = create<OfflineDataState>((set) => ({
  stale: {},
  markStale: (key, savedAt) => set((s) => (s.stale[key] === savedAt ? s : { stale: { ...s.stale, [key]: savedAt } })),
  markFresh: (key) => set((s) => {
    if (!(key in s.stale)) return s;
    const next = { ...s.stale };
    delete next[key];
    return { stale: next };
  }),
}));

// --- Чтение/запись кэша ---------------------------------------------------------

async function readCached<T>(key: string): Promise<CachedEntry<T> | undefined> {
  return (await idbGet<CachedEntry<T>>(PREFIX + key)) as CachedEntry<T> | undefined;
}

async function writeCached<T>(key: string, data: T): Promise<void> {
  try {
    await idbSet<CachedEntry<T>>(PREFIX + key, { data, savedAt: Date.now() });
  } catch (e) {
    log.warn('сохранение кэша не удалось', { key, error: e });
  }
}

/**
 * Чтение «через кэш»: сервер → сохранить; офлайн/транспортный сбой → последнее
 * сохранённое (и пометка stale для баннера). Нет сохранённого — ошибка исходного
 * запроса (онлайн) либо явная ошибка автономного режима.
 */
export async function readThrough<T>(key: string, fetcher: () => Promise<T>): Promise<ReadResult<T>> {
  let attemptError: unknown = null;
  if (!isEffectivelyOffline()) {
    try {
      const data = await fetcher();
      void writeCached(key, data);
      useOfflineDataStore.getState().markFresh(key);
      return { data, fromCache: false, savedAt: Date.now() };
    } catch (e) {
      if (!isTransportError(e)) throw e;
      attemptError = e;
      markConnectionOffline();
      markLinkDown(true);
    }
  }
  const cached = await readCached<T>(key);
  if (cached) {
    useOfflineDataStore.getState().markStale(key, cached.savedAt);
    return { data: cached.data, fromCache: true, savedAt: cached.savedAt };
  }
  if (attemptError) throw attemptError;
  throw new Error('Автономный режим: нет сохранённых данных для этого экрана');
}
